/**
 * `BACKUP CAREER` — the name prompt, and nothing else.
 *
 * ## What the original does, and why this needs no screen of its own to undo on
 *
 * The manual is explicit that a backup is a **duplicate pilot**, not a save
 * slot: *"After pressing Backup Career, enter the name under which you wish to
 * backup the career. If the career roster is full, you will be prompted to
 * delete another pilot or to cancel. This backup career may be restored from
 * the Continue A Career option in the Career Menu."* So the restore half is
 * already built and always has been — the copy appears on the roster
 * `careerRosterScreen` draws, and SELECT flies it. There is no RESTORE button
 * anywhere in the original, and the AERODROME board has no ninth slot for one.
 *
 * The note beneath that paragraph is the whole point of the feature, and it is
 * the sentence the port used to contradict: *"A pilot's most current career
 * progress is automatically saved upon exiting the Aerodrome menu. Backup
 * Career is used to save a separate version of the current career for later
 * restoring. This is useful for undoing a mistake you've made in a mission. If
 * you die or perform an act that damages your career, you will have the option
 * of restoring the backup career and replaying the mission until you are
 * satisfied with the outcome."* The autosave is not the backup; it is the thing
 * the backup exists to undo.
 *
 * ## What is measured and what is not
 *
 * `ENTER BACKUP PILOT NAME?` is verbatim — `PS.EXE`'s own prompt, recorded in
 * `docs/game/campaign.md`. No capture of this screen exists, so the *layout* is
 * inference: it reuses `career-enlist-name.png`'s measured dialog and field
 * rects, which are the only name-entry geometry the corpus gives, over the
 * aerodrome backdrop it is reached from.
 */

import { ROSTER_FULL_MESSAGE } from "../../career/career-store.ts";
import type { Rect } from "../render/draw-ops.ts";
import type { FontAtlas } from "../render/text-metrics.ts";
import { ENLIST_DIALOG, ENLIST_FIELD } from "./career-screens.ts";
import type { MenuAction } from "./menu-model.ts";
import { actionRow } from "./panel-parts.ts";
import { dialogRow, type ShellDialog, type ShellScreen } from "./shell-screen.ts";

const command = (name: string): MenuAction => ({ kind: "command", command: name });

/** `career-enlist-name.png`'s own dialog and well, reused — see the module doc on why this is inference. */
export const BACKUP_DIALOG: Rect = ENLIST_DIALOG;
export const BACKUP_FIELD: Rect = ENLIST_FIELD;

/** Verbatim `PS.EXE`, via `docs/game/campaign.md`'s string dump. */
export const BACKUP_PROMPT = "ENTER BACKUP PILOT NAME?";

export interface BackupScreenContext {
  /** What the player has typed so far. */
  readonly name: string;
  /** The aerodrome backdrop his service uses, so the screen sits where it is reached from. */
  readonly background: string;
  readonly emblem: "roundel" | "cross";
}

/**
 * The refusal, as the manual describes it: *"If the career roster is full, you
 * will be prompted to delete another pilot or to cancel."* Two rows, both of
 * them real doors — DELETE A PILOT opens the same roster CONTINUE A CAREER
 * does, which is where the original's DELETE lives.
 */
export function rosterFullDialog(): ShellDialog {
  return {
    title: "BACKUP CAREER",
    body: [ROSTER_FULL_MESSAGE],
    rows: [
      dialogRow("delete", "DELETE A PILOT", { kind: "command", command: "roster" }),
      dialogRow("cancel", "CANCEL", { kind: "close" }),
    ],
    focus: 0,
  };
}

export function backupScreen(context: BackupScreenContext, heading: FontAtlas): ShellScreen {
  const typed = context.name.trim();
  return {
    id: "career/backup",
    chrome: { kind: "panel", title: "BACKUP CAREER", emblem: context.emblem, background: context.background },
    widgets: [
      { kind: "plate", rect: BACKUP_DIALOG },
      { kind: "text", text: BACKUP_PROMPT, x: 89, y: 59, role: "prompt" },
      { kind: "well", rect: BACKUP_FIELD },
      { kind: "text", text: `${context.name}_`, x: 97, y: 71, role: "label" },
      ...actionRow(
        [
          { id: "cancel", label: "CANCEL", action: command("aerodrome") },
          // The manual's own verb. Disabled on an empty field for the same
          // reason START is on the enlistment screen: a nameless pilot is not a
          // roster entry anyone could find again.
          { id: "backup", label: "BACKUP", action: command("backup-save"), enabled: typed !== "" },
        ],
        heading,
        176,
      ),
    ],
    cancel: command("aerodrome"),
  };
}
